import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { isClerkEnabled } from '../hooks/useClerkSafe'

function StartButton({ children, className }: { children: React.ReactNode; className?: string }) {
  const target = isClerkEnabled ? '/sign-up' : '/practice'
  return <Link to={target} className={className}>{children}</Link>
}

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
}

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
}

// SVG icons for the "How It Works" steps. Text comes from i18n.
const STEP_ICONS = [
  (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  ),
  (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
    </svg>
  ),
  (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
    </svg>
  ),
]

// SVG icons for the "Features" grid. Text comes from i18n.
const FEATURE_ICONS = [
  (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
    </svg>
  ),
  (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
    </svg>
  ),
  (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
    </svg>
  ),
  (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
    </svg>
  ),
  (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  ),
  (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  ),
]

// Pricing — currency stays the same across languages, the rest comes from i18n
const FREE_PRICE = '$0'
const PRO_PRICE = '$4.99'
const PERIOD = '/mo'

function FAQSection() {
  const { t } = useTranslation()
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const items = [1, 2, 3, 4, 5, 6].map((n) => ({
    q: t(`landing.faq.q${n}`),
    a: t(`landing.faq.a${n}`),
  }))

  return (
    <section className="relative max-w-3xl mx-auto px-4 sm:px-6 py-20">
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">{t('landing.faq.heading')}</h2>
      </motion.div>
      <div className="space-y-3">
        {items.map((item, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: idx * 0.05 }}
            className="glass-card overflow-hidden"
          >
            <button
              onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
              className="w-full flex items-center justify-between p-5 text-left"
            >
              <span className="text-sm font-medium text-gray-900 dark:text-white pr-4">{item.q}</span>
              <svg
                className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${openIndex === idx ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {openIndex === idx && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="px-5 pb-5"
              >
                <p className="text-sm text-gray-600 dark:text-gray-400">{item.a}</p>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  )
}

export default function LandingPage() {
  const { t } = useTranslation()

  // i18n-driven derived data — recomputed on language change.
  const stepsT = [
    { number: '1', title: t('landing.howItWorks.step1Title'), description: t('landing.howItWorks.step1Description'), icon: STEP_ICONS[0] },
    { number: '2', title: t('landing.howItWorks.step2Title'), description: t('landing.howItWorks.step2Description'), icon: STEP_ICONS[1] },
    { number: '3', title: t('landing.howItWorks.step3Title'), description: t('landing.howItWorks.step3Description'), icon: STEP_ICONS[2] },
  ]

  const featuresT = [
    { title: t('landing.features.rulesTitle'), description: t('landing.features.rulesDescription'), icon: FEATURE_ICONS[0] },
    { title: t('landing.features.audioTitle'), description: t('landing.features.audioDescription'), icon: FEATURE_ICONS[1] },
    { title: t('landing.features.waveformTitle'), description: t('landing.features.waveformDescription'), icon: FEATURE_ICONS[2] },
    { title: t('landing.features.drillsTitle'), description: t('landing.features.drillsDescription'), icon: FEATURE_ICONS[3] },
    { title: t('landing.features.progressTitle'), description: t('landing.features.progressDescription'), icon: FEATURE_ICONS[4] },
    { title: t('landing.features.privacyTitle'), description: t('landing.features.privacyDescription'), icon: FEATURE_ICONS[5] },
  ]

  const freeFeaturesT = [
    t('landing.pricing.freeFeatures.evals'),
    t('landing.pricing.freeFeatures.feedback'),
    t('landing.pricing.freeFeatures.reciters'),
    t('landing.pricing.freeFeatures.tracking'),
  ]
  const proFeaturesT = [
    t('landing.pricing.proFeatures.unlimited'),
    t('landing.pricing.proFeatures.detailed'),
    t('landing.pricing.proFeatures.reciters'),
    t('landing.pricing.proFeatures.support'),
    t('landing.pricing.proFeatures.export'),
  ]
  const familyFeaturesT = [
    t('landing.pricing.familyFeatures.members'),
    t('landing.pricing.familyFeatures.shared'),
    t('landing.pricing.familyFeatures.everything'),
  ]

  const testimonialsT = [
    { name: t('landing.testimonials.t1Name'), role: t('landing.testimonials.t1Role'), quote: t('landing.testimonials.t1Quote') },
    { name: t('landing.testimonials.t2Name'), role: t('landing.testimonials.t2Role'), quote: t('landing.testimonials.t2Quote') },
    { name: t('landing.testimonials.t3Name'), role: t('landing.testimonials.t3Role'), quote: t('landing.testimonials.t3Quote') },
  ]

  return (
    <div className="relative overflow-hidden">
      {/* Decorative background blurs */}
      <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-40 right-1/4 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />

      {/* Hero Section */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-24 sm:pt-28 sm:pb-32">
        <motion.div
          className="text-center max-w-3xl mx-auto"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight mb-6"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
          >
            {t('landing.hero.titleStart')}{' '}
            <span className="gradient-text">{t('landing.hero.titleHighlight')}</span>
          </motion.h1>
          <motion.p
            className="text-lg sm:text-xl text-gray-500 dark:text-gray-400 mb-10 max-w-2xl mx-auto"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
          >
            {t('landing.hero.subtitle')}
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
          >
            <StartButton className="btn-primary text-base px-8 py-4 rounded-2xl shadow-lg shadow-emerald-600/20">
              {t('landing.hero.ctaPrimary')}
            </StartButton>
          </motion.div>
        </motion.div>
      </section>

      {/* How It Works */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">{t('landing.howItWorks.heading')}</h2>
          <p className="text-gray-500 dark:text-gray-400 text-lg">{t('landing.howItWorks.subheading')}</p>
        </motion.div>
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {stepsT.map((step) => (
            <motion.div
              key={step.number}
              className="glass-card p-8 text-center relative"
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-gray-900 dark:text-white text-sm font-bold">
                {step.number}
              </div>
              <div className="text-emerald-400 flex justify-center mb-4 mt-2">
                {step.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{step.title}</h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm">{step.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Features Grid */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">{t('landing.features.heading')}</h2>
          <p className="text-gray-500 dark:text-gray-400 text-lg">{t('landing.features.subheading')}</p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuresT.map((feature, idx) => (
            <motion.div
              key={feature.title}
              className="glass-card p-6 group hover:border-gray-300 dark:border-gray-700/80 transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.08 }}
              whileHover={{ y: -4 }}
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 mb-4 group-hover:bg-emerald-500/20 transition-colors">
                {feature.icon}
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">{feature.title}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">{t('landing.pricing.heading')}</h2>
          <p className="text-gray-500 dark:text-gray-400 text-lg">{t('landing.pricing.subheading')}</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* Free Plan */}
          <motion.div
            className="glass-card p-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.02 }}
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('landing.pricing.free')}</h3>
            <div className="flex items-baseline gap-1 mb-6">
              <span className="text-4xl font-extrabold text-gray-900 dark:text-white">{FREE_PRICE}</span>
              <span className="text-gray-500 dark:text-gray-400 text-sm">{PERIOD}</span>
            </div>
            <ul className="space-y-3 mb-8">
              {freeFeaturesT.map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                  <svg className="w-4 h-4 text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  {f}
                </li>
              ))}
            </ul>
            <StartButton className="w-full inline-block text-center rounded-xl border border-gray-300 dark:border-gray-700 px-6 py-3 text-sm font-semibold text-gray-600 dark:text-gray-300 transition-all hover:border-gray-500 hover:text-gray-900 dark:text-white">
              {t('landing.pricing.getStarted')}
            </StartButton>
          </motion.div>

          {/* Pro Plan */}
          <motion.div
            className="gradient-border"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="p-8 relative">
              <div className="absolute -top-px right-6 bg-gradient-to-r from-emerald-500 to-teal-400 text-gray-900 dark:text-white text-xs font-bold px-3 py-1 rounded-b-lg">
                {t('landing.pricing.mostPopular')}
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('landing.pricing.pro')}</h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-extrabold text-gray-900 dark:text-white">{PRO_PRICE}</span>
                <span className="text-gray-500 dark:text-gray-400 text-sm">{PERIOD}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {proFeaturesT.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                    <svg className="w-4 h-4 text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>
              <StartButton className="btn-primary w-full inline-block text-center">
                {t('landing.pricing.upgradeToPro')}
              </StartButton>
            </div>
          </motion.div>

          {/* Family Plan (coming soon) */}
          <motion.div
            className="glass-card p-8 opacity-70"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 0.7, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('landing.pricing.family')}</h3>
            <div className="flex items-baseline gap-1 mb-6">
              <span className="text-2xl font-bold text-gray-700 dark:text-gray-300">{t('landing.pricing.comingSoon')}</span>
            </div>
            <ul className="space-y-3 mb-8">
              {familyFeaturesT.map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                  <svg className="w-4 h-4 text-emerald-400/60 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  {f}
                </li>
              ))}
            </ul>
            <button
              type="button"
              disabled
              className="w-full inline-block text-center rounded-xl border border-gray-300 dark:border-gray-700 px-6 py-3 text-sm font-semibold text-gray-400 dark:text-gray-500 cursor-not-allowed"
            >
              {t('landing.pricing.comingSoon')}
            </button>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">{t('landing.testimonials.heading')}</h2>
          <p className="text-gray-500 dark:text-gray-400 text-lg">{t('landing.testimonials.subheading')}</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonialsT.map((tm, idx) => (
            <motion.div
              key={tm.name}
              className="glass-card p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
            >
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <svg key={i} className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 italic">"{tm.quote}"</p>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{tm.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{tm.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <FAQSection />

      {/* Final CTA */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('landing.finalCta.heading')}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-lg mb-8">
            {t('landing.finalCta.subheading')}
          </p>
          <StartButton className="btn-primary text-base px-10 py-4 rounded-2xl shadow-lg shadow-emerald-600/20">
            {t('landing.finalCta.cta')}
          </StartButton>
        </motion.div>
      </section>
    </div>
  )
}
